import { useState, useEffect } from 'react';
import { Pane, Heading, Tablist, Tab, AnnotationIcon, CalendarIcon, ClipboardIcon, PeopleIcon, PaperclipIcon, BankAccountIcon } from 'evergreen-ui';
import BulletinBoard from '../components/BulletinBoard';
import Communications from '../components/Communications';
import Archive from '../components/Archive';
import Diary from '../components/Diary';
import Guests from '../components/Guests';
import Volunteers from '../components/Volunteers';
import TurniOperatori from '../components/TurniOperatori';
import CollaborativeMembers from '../components/CollaborativeMembers';
import PagamentiDirettore from '../components/Pagamenti/PagamentiDirettore';
import PagamentiCollaboratore from '../components/Pagamenti/PagamentiCollaboratore';
import { useAuth } from '../services/authContext';
import { fetchAnnouncements, fetchCommunications, fetchGuests, fetchDiaryEntries } from '../services/commonServices';
import { fetchAttachments as fetchAttachmentsDirector, fetchCollaborators } from '../services/managementServices';
import { fetchAttachments as fetchAttachmentsEducator } from '../services/educationServices';

const AREA_LABELS = {
  direttore: 'Area direttore',
  educatore: 'Area educatori',
  operatore: 'Area operatori',
};

const DashboardPage = () => {
  const { user } = useAuth();
  const role = user?.role ?? 'operatore';

  const isDirector = role === 'direttore';
  const isEducator = role === 'educatore';
  const isOperator = role === 'operatore';

  const [announcements, setAnnouncements] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [guests, setGuests] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const common = await Promise.all([
          fetchAnnouncements(),
          fetchCommunications(),
          fetchGuests(),
          fetchDiaryEntries(),
        ]);
        setAnnouncements(common[0]);
        setCommunications(common[1]);
        setGuests(common[2]);
        setDiaryEntries(common[3] || []);

        if (isDirector) {
          const [attachmentsData, collaboratorsData] = await Promise.all([
            fetchAttachmentsDirector(),
            fetchCollaborators(),
          ]);
          setAttachments(attachmentsData);
          setCollaborators(collaboratorsData);
        } else if (isEducator) {
          const attachmentsData = await fetchAttachmentsEducator();
          setAttachments(attachmentsData);
        }
      } catch (error) {
        console.error('Errore nel caricamento dei dati:', error);
      }
    };

    fetchData();
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const allTabs = [
    {
      label: 'Bacheca',
      icon: ClipboardIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <BulletinBoard
          announcements={announcements}
          setAnnouncements={setAnnouncements}
          isOperator={isOperator}
          isDirector={isDirector}
          isEducator={isEducator}
        />
      ),
    },
    {
      label: 'Comunicazioni',
      icon: AnnotationIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Communications
          communications={communications}
          setCommunications={setCommunications}
          isOperator={isOperator}
          isDirector={isDirector}
          collaborators={collaborators}
        />
      ),
    },
    {
      label: 'Collaboratori',
      icon: PeopleIcon,
      roles: ['direttore'],
      component: (
        <CollaborativeMembers
          collaborators={collaborators}
          setCollaborators={setCollaborators}
        />
      ),
    },
    {
      label: 'Archivio',
      icon: PaperclipIcon,
      roles: ['direttore', 'educatore'],
      component: (
        <Archive
          isDirector={isDirector}
          attachments={attachments}
          setAttachments={setAttachments}
        />
      ),
    },
    {
      label: 'Ospiti',
      icon: PeopleIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Guests
          guests={guests}
          setGuests={setGuests}
          isDirector={isDirector}
        />
      ),
    },
    {
      label: 'Volontari',
      icon: PeopleIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Volunteers
          isDirector={isDirector}
          volunteers={volunteers}
          setVolunteers={setVolunteers}
        />
      ),
    },
    {
      label: 'Turni',
      icon: CalendarIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <TurniOperatori
          isDirector={isDirector}
          isEducator={isEducator}
          isOperator={isOperator}
        />
      ),
    },
    {
      label: 'Diario',
      icon: CalendarIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Diary
          guests={guests}
          diaryEntries={diaryEntries}
          setDiaryEntries={setDiaryEntries}
          isOperator={isOperator}
          isDirector={isDirector}
          isEducator={isEducator}
        />
      ),
    },
    {
      label: 'Pagamenti',
      icon: BankAccountIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: isDirector
        ? <PagamentiDirettore collaborators={collaborators} />
        : <PagamentiCollaboratore />,
    },
  ];

  const tabs = allTabs.filter((t) => t.roles.includes(role));

  const safeIndex = selectedIndex < tabs.length ? selectedIndex : 0;

  return (
    <Pane padding={32}>
      <Heading size={800} marginBottom={24}>
        {AREA_LABELS[role] ?? 'Area riservata'}
      </Heading>

      <Tablist marginBottom={16} flexBasis={240} flexShrink={0} marginRight={24}>
        {tabs.map((tab, index) => (
          <Tab
            key={tab.label}
            id={tab.label}
            onSelect={() => setSelectedIndex(index)}
            isSelected={safeIndex === index}
          >
            {tab.icon && <tab.icon marginRight={8} />}
            {tab.label}
          </Tab>
        ))}
      </Tablist>

      <Pane>
        {tabs[safeIndex]?.component}
      </Pane>
    </Pane>
  );
};

export default DashboardPage;
